import { useMemo } from "react";
import moment from "moment-timezone";
import { useCompany } from "../../../../utils/hooks/company";

// cron's CRON_HIBOB_SYNC default ("0 3 * * *", UTC). The API does not return it.
export const NIGHTLY_SYNC_UTC_HOUR = 3;

export const toMoment = (value) => {
	const date = value ? moment(value) : null;
	return date?.isValid() ? date : null;
};

export const count = (value) => (Number.isFinite(value) && value > 0 ? value : 0);

/** The next nightly sync, in the viewer's local time. */
export const nextNightlySync = (now = moment()) => {
	const next = moment.utc(now).startOf("day").add(NIGHTLY_SYNC_UTC_HOUR, "hours");
	if (!next.isAfter(now)) {
		next.add(1, "day");
	}
	return next.local();
};

/** "Tonight" for an evening today or an early-morning tomorrow; else Today / Tomorrow / a date. */
export const describeNextSync = (next, now, t, formats) => {
	const time = formats.time(next);
	const today = now.clone().startOf("day");
	const tomorrow = today.clone().add(1, "day");
	if ((next.isSame(today, "day") && next.hour() >= 18) || (next.isSame(tomorrow, "day") && next.hour() < 6)) {
		return t("hibob-when-tonight", { time });
	}
	if (next.isSame(today, "day")) {
		return t("hibob-when-today", { time });
	}
	if (next.isSame(tomorrow, "day")) {
		return t("hibob-when-tomorrow", { time });
	}
	return formats.dateTime(next);
};

export const describeDuration = (start, end, t) => {
	const seconds = Math.max(0, Math.round(end.diff(start) / 1000));
	if (seconds < 60) {
		return t("hibob-duration-seconds", { n: seconds });
	}
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) {
		return t("hibob-duration-minutes", { n: minutes });
	}
	return t("hibob-duration-hours", { h: Math.floor(minutes / 60), m: minutes % 60 });
};

/** Company date/time formats; times are shown in the viewer's local time zone. */
export const useFormats = () => {
	const company = useCompany();
	const dateFormat = company?.settings?.dateFormat || "MM/DD/YYYY";
	const timeFormat = company?.settings?.timeFormat || "hh:mm A";
	return useMemo(
		() => ({
			date: (m) => m.clone().local().format(dateFormat),
			time: (m) => m.clone().local().format(timeFormat),
			dateTime: (m) => m.clone().local().format(`${dateFormat} ${timeFormat}`),
			// Same day: just the time; otherwise date and time.
			when: (m) =>
				m.clone().local().isSame(moment(), "day")
					? m.clone().local().format(timeFormat)
					: m.clone().local().format(`${dateFormat} ${timeFormat}`),
			relative: (m) => m.fromNow(),
		}),
		[dateFormat, timeFormat],
	);
};
